import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { useClient } from 'jsonapi-react';
import { TextareaAutosize } from '@/components/ui/textarea-autosize';
import toast from 'react-hot-toast';
import { Loader } from '@/components/custom/loader';
import { useTranslation } from 'react-i18next';
import promiseRequest from '@/utils/promiseToast';
import { DashboardContent } from '@/layouts/dashboard';
import { PageContent, Page, PageSidebar } from '@/components/custom/page';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ButtonGroup } from '@/components/ui/button-group';
import { ActivityCardBody, ActivityCardDescription, ActivityCardHeader, ActivityCardHeaderContent, ActivityCardInfo, ActivityCardTitle } from '@/components/custom/activity';
import { FormField } from '@/components/custom/form';
import { InputFile } from '@/components/ui/input';

export default function ComplementaryActivityDetailsDefault(props) {
  const navigate = useNavigate();
  const { uri } = props;

  const { t } = useTranslation();
  const client = useClient();
  const { promiseToast, isSubmitting } = promiseRequest();
  const [showModal, setShowModal] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [activity, setActivity] = useState();
  const [fileType, setFileType] = useState(t('activity.toggleFile'));

  useEffect(() => {
    getActivity();
  }, []);

  const getActivity = async () => {
    try {
      setIsLoading(true);
      const { data } = await client.fetch(['activities', props.id]);
      setActivity(data);
      setIsLoading(false);
    } catch (e) {
      console.error(e);
      toast.error(t('toast.errorGetActivity'));
    }
  };

  const form = useFormik({
    initialValues: { file: null, url: null, comment: '' },
    onSubmit: () => confirmSubmit()
  });

  const confirmSubmit = () => {
    setShowModal(true);
  };

  const toBase64 = file =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });

  const submitForm = async () => {
    if (isSubmitting) {
      return false;
    }

    const { values } = form;
    const parsedForm = {
      ...values,
      file: undefined,
      url: undefined,
      comment: undefined,
      'comments-attributes': [{ body: values?.comment }],
      'activity-file-attributes': { url: null, file: null }
    };

    if (values.file && values.file !== '') {
      parsedForm['activity-file-attributes'].file = await toBase64(values.file);
    }

    if (values.url && values.url !== '') {
      parsedForm['activity-file-attributes'].url = values.url;
    }

    const onSuccess = () => {
      setShowModal(false);
      navigate(-1);
    };

    promiseToast({
      url: `activities/${props.id}/activity_students`,
      request: parsedForm,
      onSuccess,
      successText: t('toast.successSubmitActivity'),
      errorText: t('toast.errorSubmitActivity')
    });
  };

  const breadcrumbs = {
    title: 'Atividades',
    nav: [
      {
        route: '/atividade-complementar',
        name: 'Atividade Complementar',
        isActive: false
      },
      {
        route: uri,
        name: activity?.title,
        isActive: true
      }
    ]
  };

  return isLoading ? (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <Loader />
    </DashboardContent>
  ) : (
    <DashboardContent
      variant="plain"
      breadcrumbs={breadcrumbs}
    >
      <Page className="animate-fade-in">
        <PageContent
          bg
          className="gap-0"
        >
          <ActivityCardHeader>
            <ActivityCardHeaderContent>
              {/* <span className="activity-card__tag">
                {t('textsCommon.availableUntil')}: {moment(activity['finish-at']).format(t('datesForm.formatDate'))}
              </span> */}
              <ActivityCardTitle size="md">{activity.title}</ActivityCardTitle>

              <ActivityCardInfo>
                {t('textsCommon.module')} {activity.modulus} | {t('textsCommon.unit')} {activity.unit} | {activity['class-hours'] < 2 ? `${activity['class-hours']} ${t('tasks.hour')}` : `${activity['class-hours']} ${t('tasks.hour')}`}
              </ActivityCardInfo>
            </ActivityCardHeaderContent>

            {activity['activity-file'] && (
              <Button
                variant="outline"
                className="shrink-0"
                asChild
              >
                <a
                  href={activity['activity-file']?.url || activity['activity-file']?.file}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('button.supportMaterial')}
                </a>
              </Button>
            )}
          </ActivityCardHeader>

          <ActivityCardBody>
            <ActivityCardDescription>{activity.description}</ActivityCardDescription>

            <form
              method="post"
              onSubmit={form.handleSubmit}
              className="space-y-5"
            >
              <ButtonGroup className="mb-4">
                {[t('activity.toggleFile'), 'URL'].map((alternative, index) => {
                  const isActive = alternative === fileType;

                  return (
                    <Button
                      key={index}
                      variant={isActive ? 'default' : 'outline'}
                      size="sm"
                      type="button"
                      onClick={() => {
                        if (alternative === t('activity.toggleFile')) {
                          form.setFieldValue('url', null);
                        } else {
                          form.setFieldValue('file', null);
                        }
                        setFileType(alternative);
                      }}
                      aria-pressed={isActive}
                    >
                      {alternative}
                    </Button>
                  );
                })}
              </ButtonGroup>

              {fileType === t('activity.toggleFile') && (
                <InputFile
                  file={form.values.file}
                  name="file"
                  id="file"
                  onChange={e => form.setFieldValue('file', e.target.files[0])}
                />
              )}

              {fileType === 'URL' && (
                <FormField
                  id="url"
                  name="url"
                  type="url"
                  aria-label={t('activity.placeholderSentUrl')}
                  placeholder={t('activity.placeholderSentUrl')}
                  onChange={form.handleChange}
                  defaultValue={form.values.url}
                />
              )}

              <FormField
                label="*Realize um comentário para enviar com sucesso"
                id="comment"
              >
                <TextareaAutosize
                  id="comment"
                  name="comment"
                  placeholder={t('activity.placeholderWriteComment')}
                  onChange={form.handleChange}
                  defaultValue={form.values.comment}
                />
              </FormField>

              <Button
                wide
                type="submit"
              >
                {t('button.submit')}
              </Button>
            </form>
          </ActivityCardBody>
        </PageContent>

        <PageSidebar />
      </Page>

      <Dialog
        open={showModal}
        onOpenChange={setShowModal}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('activity.warningSubmit')}</DialogTitle>
          </DialogHeader>

          <div className="flex items-center justify-center gap-3">
            <Button
              variant="outline"
              wide
              onClick={() => setShowModal(false)}
              type="button"
            >
              {t('button.cancel')}
            </Button>
            <Button
              wide
              onClick={() => submitForm()}
              type="button"
              disabled={isSubmitting}
            >
              {t('button.submit')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </DashboardContent>
  );
}
